import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/auth-context';
import { SentimentStrip } from '../components/sentiment';
import { SiteFooter } from '../components/site-footer';
import { SiteHeader } from '../components/site-header';
import { ApiError, createWatchlist, type Watchlist } from '../lib/api';
import { usePageTitle } from '../lib/page-title';
import { formatScore, toneFor } from '../lib/sentiment';
import {
  summarizeWatchlist,
  useWatchlistAction,
  useWatchlistCache,
  useWatchlists,
  type ScoredTicker,
} from '../lib/watchlists';

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function CreateWatchlistForm({ token, atLimit }: { token: string; atLimit: boolean }) {
  const inputId = useId();
  const navigate = useNavigate();
  const cache = useWatchlistCache(token);
  const [name, setName] = useState('');
  const { busy, error, run } = useWatchlistAction();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const created: { id?: number } = {};
    const ok = await run(async () => {
      const watchlist = await createWatchlist(token, name.trim());
      cache.added(watchlist);
      created.id = watchlist.id;
    });
    // A new list is empty; its own page opens ready for adding tickers.
    if (ok && created.id !== undefined) await navigate(`/watchlists/${created.id}`);
  }

  return (
    <form className="watchlist-create" onSubmit={(event) => void submit(event)}>
      <label htmlFor={inputId}>New watchlist</label>
      <div className="inline-form">
        <input
          id={inputId}
          type="text"
          placeholder="e.g. Semiconductors"
          maxLength={60}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={atLimit}
        />
        <button type="submit" disabled={busy || atLimit || !name.trim()}>
          Create watchlist
        </button>
      </div>
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}

function Extreme({ label, ticker }: { label: string; ticker: ScoredTicker }) {
  return (
    <span className="watchlist-extreme">
      <span className="watchlist-extreme-label">{label}</span>
      <strong>{ticker.symbol}</strong>
      <span className={`tone-${toneFor(ticker.latest.score)}`}>
        {formatScore(ticker.latest.score)}
      </span>
    </span>
  );
}

/** One list at a glance: how its tickers are doing together, and its extremes. */
function WatchlistRow({ watchlist }: { watchlist: Watchlist }) {
  const { average, ranked, highest, lowest } = summarizeWatchlist(watchlist);
  const count = watchlist.tickers.length;

  let detail;
  if (count === 0) {
    detail = <span className="watchlist-row-note">No tickers yet</span>;
  } else if (!average || !highest || !lowest) {
    detail = <span className="watchlist-row-note">No scores yet</span>;
  } else {
    detail = (
      <span className="watchlist-extremes">
        {ranked.length > 1 ? (
          <>
            <Extreme label="Highest" ticker={highest} />
            <Extreme label="Lowest" ticker={lowest} />
          </>
        ) : (
          <Extreme label="Only score" ticker={highest} />
        )}
      </span>
    );
  }

  return (
    <li className="watchlist-row">
      <span className="watchlist-row-name">
        <Link className="watchlist-row-link" to={`/watchlists/${watchlist.id}`}>
          {watchlist.name}
        </Link>
        <span className="watchlist-row-count">
          {plural(count, 'ticker')}
          {average && ranked.length < count ? `, ${ranked.length} scored` : ''}
        </span>
      </span>
      <span className="watchlist-row-score">
        {average ? (
          <>
            <span className={`watchlist-row-figure tone-${toneFor(average.score)}`}>
              <span className="visually-hidden">Average score </span>
              {formatScore(average.score)}
            </span>
            <SentimentStrip {...average} />
          </>
        ) : (
          <span className="watchlist-row-figure watchlist-row-empty">
            <span aria-hidden="true">–</span>
            <span className="visually-hidden">No average yet</span>
          </span>
        )}
      </span>
      {detail}
    </li>
  );
}

/** Every named list with its average; tickers are managed on each list's own page. */
export function WatchlistsPage() {
  usePageTitle('Watchlists');
  const { session } = useAuth();
  const token = session?.access_token ?? '';
  const watchlists = useWatchlists(token);

  let body;
  if (watchlists.isPending) {
    body = (
      <p className="section-state" role="status">
        Loading your watchlists…
      </p>
    );
  } else if (watchlists.isError) {
    body =
      watchlists.error instanceof ApiError && watchlists.error.status === 401 ? (
        <p className="section-state state-error" role="alert">
          <span>Your session has expired.</span>
          <Link className="text-link" to="/signin">
            Sign in again
          </Link>
        </p>
      ) : (
        <p className="section-state state-error" role="alert">
          <span>Could not load your watchlists.</span>
          <button
            type="button"
            className="button-quiet button-inline"
            onClick={() => void watchlists.refetch()}
          >
            Retry
          </button>
        </p>
      );
  } else {
    const { watchlists: lists, max_watchlists: maxLists } = watchlists.data;
    body = (
      <>
        <CreateWatchlistForm token={token} atLimit={lists.length >= maxLists} />
        <p className="catalog-count" role="status">
          {lists.length} of {maxLists} watchlists
        </p>
        {lists.length === 0 ? (
          <p className="section-state">
            You have no watchlists yet. Name one above, then add tickers to it.
          </p>
        ) : (
          <>
            <ul className="watchlist-rows" aria-label="Your watchlists">
              {lists.map((watchlist) => (
                <WatchlistRow key={watchlist.id} watchlist={watchlist} />
              ))}
            </ul>
            <p className="watchlist-footnote">
              A list’s score is the average of its tickers’ latest daily scores.
            </p>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main className="page">
        <div className="page-head">
          <h1>Watchlists</h1>
          <p className="lede">
            Group the tickers you follow into named lists and see how each list is doing as a whole.
          </p>
        </div>
        {body}
      </main>
      <SiteFooter />
    </>
  );
}
